import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { PlusIcon, EyeIcon, PencilIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";

const MySwal = withReactContent(Swal);

// Modal Component for Add/Edit Blog
const BlogModal = ({ isOpen, onClose, onSubmit, initialData, isEdit }) => {
    const [formData, setFormData] = useState(
        initialData || {
            title_ar: "",
            title_en: "",
            details_ar: "",
            details_en: "",
            image: null,
        }
    );
    const [errors, setErrors] = useState({});
    const [imagePreview, setImagePreview] = useState(
        isEdit && initialData?.image
            ? `https://ligasos-laravel.ligasos.co/storage/app/public/${initialData.image}`
            : null
    );
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        if (name === "image" && files[0]) {
            setFormData({ ...formData, image: files[0] });
            setImagePreview(URL.createObjectURL(files[0]));
        } else {
            setFormData({ ...formData, [name]: value });
        }
        setErrors({ ...errors, [name]: "" });
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.title_ar) newErrors.title_ar = "Arabic Title is required";
        if (!formData.title_en) newErrors.title_en = "English Title is required";
        if (!formData.details_ar) newErrors.details_ar = "Arabic Details are required";
        if (!formData.details_en) newErrors.details_en = "English Details are required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            setIsSubmitting(true);
            const data = new FormData();
            data.append("title_ar", formData.title_ar);
            data.append("title_en", formData.title_en);
            data.append("details_ar", formData.details_ar);
            data.append("details_en", formData.details_en);
            if (formData.image) data.append("image", formData.image);
            await onSubmit(data);
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[300]">
            <div className="bg-[#0B2C3D] p-8 rounded-2xl shadow-2xl w-full max-w-lg transform transition-all duration-300">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-extrabold text-white">{isEdit ? "Edit Blog" : "Add Blog"}</h3>
                    <button onClick={onClose} className="text-white hover:text-[#B39359] transition-colors duration-200">
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>
                <form onSubmit={handleFormSubmit} className="space-y-5">
                    <div>
                        <label className="block text-sm font-medium text-white">Title (Arabic)</label>
                        <input
                            type="text"
                            name="title_ar"
                            value={formData.title_ar}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50"
                            placeholder="Enter Arabic title"
                        />
                        {errors.title_ar && <p className="text-red-400 text-sm mt-1">{errors.title_ar}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Title (English)</label>
                        <input
                            type="text"
                            name="title_en"
                            value={formData.title_en}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50"
                            placeholder="Enter English title"
                        />
                        {errors.title_en && <p className="text-red-400 text-sm mt-1">{errors.title_en}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Details (Arabic)</label>
                        <textarea
                            name="details_ar"
                            value={formData.details_ar}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50 min-h-[100px]"
                            placeholder="Enter Arabic details"
                        />
                        {errors.details_ar && <p className="text-red-400 text-sm mt-1">{errors.details_ar}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Details (English)</label>
                        <textarea
                            name="details_en"
                            value={formData.details_en}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50 min-h-[100px]"
                            placeholder="Enter English details"
                        />
                        {errors.details_en && <p className="text-red-400 text-sm mt-1">{errors.details_en}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Image (optional)</label>
                        <input
                            type="file"
                            name="image"
                            accept="image/*"
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-[#B39359] file:text-white hover:file:bg-[#D4A373] transition-colors duration-300 disabled:opacity-50"
                        />
                        {imagePreview && (
                            <img
                                src={imagePreview}
                                alt="Preview"
                                className="mt-3 w-20 h-20 rounded-full object-cover border-2 border-[#B39359]"
                            />
                        )}
                    </div>
                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isSubmitting}
                            className="py-2 px-5 rounded-lg bg-gray-600 text-white hover:bg-gray-700 transition-colors duration-300 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="py-2 px-5 rounded-lg bg-[#B39359] text-white hover:bg-[#D4A373] transition-colors duration-300 disabled:opacity-50 flex items-center gap-2"
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                                    Processing...
                                </>
                            ) : (
                                <>{isEdit ? "Update" : "Add"}</>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Modal Component for Viewing Blog Details
const BlogViewModal = ({ isOpen, onClose, blog }) => {
    if (!isOpen || !blog) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[300]">
            <div className="bg-[#0B2C3D] p-8 rounded-2xl shadow-2xl w-full max-w-lg transform transition-all duration-300">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-extrabold text-white">Blog Details</h3>
                    <button onClick={onClose} className="text-white hover:text-[#B39359] transition-colors duration-200">
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-white">Title (Arabic)</label>
                        <p className="mt-1 text-white">{blog.title_ar || "N/A"}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Title (English)</label>
                        <p className="mt-1 text-white">{blog.title_en || "N/A"}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Details (Arabic)</label>
                        <p className="mt-1 text-white">{blog.details_ar || "N/A"}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Details (English)</label>
                        <p className="mt-1 text-white">{blog.details_en || "N/A"}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Image</label>
                        {blog.image ? (
                            <img
                                src={`https://ligasos-laravel.ligasos.co/storage/app/public/${blog.image}`}
                                alt={blog.title_en}
                                className="mt-3 w-32 h-32 object-cover border-2 border-[#B39359]"
                                onError={(e) => (e.target.src = "https://via.placeholder.com/128")}
                            />
                        ) : (
                            <p className="mt-1 text-white">No Image</p>
                        )}
                    </div>
                    <div className="flex justify-end">
                        <button
                            type="button"
                            onClick={onClose}
                            className="py-2 px-5 rounded-lg bg-gray-600 text-white hover:bg-gray-700 transition-colors duration-300"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default function Blog() {
    const [blogs, setBlogs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [editBlog, setEditBlog] = useState(null);
    const [selectedBlog, setSelectedBlog] = useState(null);
    const token = localStorage.getItem("token");

    // Fetch all blogs
    useEffect(() => {
        const fetchBlogs = async () => {
            setIsLoading(true);
            try {
                const response = await axios.get("https://ligasos-laravel.ligasos.co/api/blogs", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setBlogs(response.data);
            } catch (err) {
                MySwal.fire({
                    icon: "error",
                    title: "Error",
                    text: "Failed to fetch blogs. Please try again.",
                    confirmButtonColor: "#B39359",
                });
            } finally {
                setIsLoading(false);
            }
        };
        fetchBlogs();
    }, [token]);

    // Handle Add Blog
    const handleAddBlog = async (data) => {
        setIsLoading(true);
        try {
            const response = await axios.post(
                "https://ligasos-laravel.ligasos.co/api/blogs",
                data,
                { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
            );
            setBlogs([...blogs, response.data.data]);
            setIsAddModalOpen(false);
            MySwal.fire({
                icon: "success",
                title: "Success",
                text: "Blog added successfully!",
                confirmButtonColor: "#B39359",
                timer: 1500,
                showConfirmButton: false,
            });
        } catch (err) {
            MySwal.fire({
                icon: "error",
                title: "Error",
                text: err.response?.data?.message || "Failed to add blog.",
                confirmButtonColor: "#B39359",
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Handle Edit Blog
    const handleEditBlog = async (data) => {
        setIsLoading(true);
        try {
            const response = await axios.post(
                `https://ligasos-laravel.ligasos.co/api/blogs/${editBlog.id}?_method=PUT`,
                data,
                { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
            );
            setBlogs(blogs.map((b) => (b.id === editBlog.id ? response.data.data : b)));
            setIsEditModalOpen(false);
            setEditBlog(null);
            MySwal.fire({
                icon: "success",
                title: "Success",
                text: "Blog updated successfully!",
                confirmButtonColor: "#B39359",
                timer: 1500,
                showConfirmButton: false,
            });
        } catch (err) {
            MySwal.fire({
                icon: "error",
                title: "Error",
                text: err.response?.data?.message || "Failed to update blog.",
                confirmButtonColor: "#B39359",
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Handle Delete Blog
    const handleDeleteBlog = async (id) => {
        const result = await MySwal.fire({
            title: "Are you sure?",
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Delete",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#B39359",
            cancelButtonColor: "#dc2626",
        });

        if (result.isConfirmed) {
            setIsLoading(true);
            try {
                await axios.delete(`https://ligasos-laravel.ligasos.co/api/blogs/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setBlogs(blogs.filter((b) => b.id !== id));
                MySwal.fire({
                    icon: "success",
                    title: "Deleted",
                    text: "Blog deleted successfully!",
                    confirmButtonColor: "#B39359",
                    timer: 1500,
                    showConfirmButton: false,
                });
            } catch (err) {
                MySwal.fire({
                    icon: "error",
                    title: "Error",
                    text: err.response?.data?.message || "Failed to delete blog.",
                    confirmButtonColor: "#B39359",
                });
            } finally {
                setIsLoading(false);
            }
        }
    };

    // Handle View Blog
    const handleViewBlog = async (id) => {
        setIsLoading(true);
        try {
            const response = await axios.get(`https://ligasos-laravel.ligasos.co/api/blogs/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setSelectedBlog(response.data);
            setIsViewModalOpen(true);
        } catch (err) {
            MySwal.fire({
                icon: "error",
                title: "Error",
                text: err.response?.data?.message || "Failed to fetch blog details.",
                confirmButtonColor: "#B39359",
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <section className="p-8 min-h-screen">
            <div className="">
                <div className="flex justify-between items-center mb-8">
                    <h2 className="text-4xl font-extrabold text-[#0B2C3D] tracking-tight">Blogs</h2>
                    <button
                        onClick={() => setIsAddModalOpen(true)}
                        disabled={isLoading}
                        className="flex items-center gap-2 bg-[#B39359] text-white font-semibold py-3 px-6 rounded-lg hover:bg-[#D4A373] transition-colors duration-300 disabled:opacity-50 shadow-md"
                    >
                        <PlusIcon className="w-5 h-5" />
                        Add Blog
                    </button>
                </div>

                {isLoading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#B39359]"></div>
                    </div>
                ) : blogs.length === 0 ? (
                    <p className="text-center text-gray-500 text-lg font-medium">No blogs found.</p>
                ) : (
                    <div className="overflow-x-auto bg-white rounded-xl shadow-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-[#0B2C3D] sticky top-0 z-10">
                                <tr>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">Image</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">Title (Arabic)</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">Title (English)</th>
                                    <th className="px-6 py-4 text-right text-sm font-semibold text-white uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {blogs.map((blog, index) => (
                                    <tr
                                        key={blog.id}
                                        className={`hover:bg-gray-50 transition-colors duration-200 ${index % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                                    >
                                        <td className="px-6 py-4">
                                            {blog.image ? (
                                                <img
                                                    src={`https://ligasos-laravel.ligasos.co/storage/app/public/${blog.image}`}
                                                    alt={blog.title_en}
                                                    className="w-12 h-12 object-cover border-2 border-[#B39359]"
                                                    onError={(e) => (e.target.src = "https://via.placeholder.com/40")}
                                                />
                                            ) : (
                                                <div className="w-12 h-12 bg-gray-200 flex items-center justify-center text-gray-500 text-xs font-medium">
                                                    No Image
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-sm font-medium text-[#0B2C3D]">{blog.title_ar || "N/A"}</td>
                                        <td className="px-6 py-4 text-sm font-medium text-[#0B2C3D]">{blog.title_en || "N/A"}</td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-2">
                                                <button
                                                    onClick={() => handleViewBlog(blog.id)}
                                                    className="p-2 text-[#0B2C3D] hover:bg-[#0B2C3D] hover:text-white rounded-full transition-colors duration-300"
                                                    disabled={isLoading}
                                                >
                                                    <EyeIcon className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setEditBlog(blog);
                                                        setIsEditModalOpen(true);
                                                    }}
                                                    className="p-2 text-[#0B2C3D] hover:bg-[#0B2C3D] hover:text-white rounded-full transition-colors duration-300"
                                                    disabled={isLoading}
                                                >
                                                    <PencilIcon className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteBlog(blog.id)}
                                                    className="p-2 text-red-600 hover:bg-red-600 hover:text-white rounded-full transition-colors duration-300"
                                                    disabled={isLoading}
                                                >
                                                    <TrashIcon className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modals */}
            <BlogModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onSubmit={handleAddBlog}
                isEdit={false}
            />
            <BlogModal
                isOpen={isEditModalOpen}
                onClose={() => {
                    setIsEditModalOpen(false);
                    setEditBlog(null);
                }}
                onSubmit={handleEditBlog}
                initialData={editBlog}
                isEdit={true}
            />
            <BlogViewModal
                isOpen={isViewModalOpen}
                onClose={() => {
                    setIsViewModalOpen(false);
                    setSelectedBlog(null);
                }}
                blog={selectedBlog}
            />
        </section>
    );
}